import React, { useContext, useEffect, useRef } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useTable, useSortBy, useFlexLayout } from 'react-table';
import {
  TableColumnDefinitions,
  TableHeaderCellType,
} from '~/components/organism/Table/types';
import { FlowListData } from '~/components/page/Automation/v2/util/composeFlowListData';
import Catalog from '~/Catalog';
import { ACTION_COLUMN_ACCESSOR } from '~/components/organism/Table/components/Header/utils/asActionCell';
import FlowActionColumnCell from '../FlowActionColumnCell';
import { isInitialLoadStatus } from '~/graphql/ApolloConstants';
import TEST_ID from './index.testid';
import Row from '~/components/organism/Table/components/Row';
import HeaderLarge from '~/components/organism/Table/components/Header/components/Large';
import LoadingBody from '~/components/organism/Table/components/LoadingBody';
import BodyInner from '~/components/organism/Table/components/Body/components/Inner';
import BodyOuter from '~/components/organism/Table/components/Body/components/Outer';
import useSortSettings from '~/hooks/useSortSettings';
import { MAIN_CONTENT_WIDTH } from '~/components/organism/NavigationFrame/constants';
import { flowActions } from '~/components/page/Automation/v2/state/actions';
import { useResetRecoilState } from 'recoil';
import NoResultSection from '~/components/page/NoResultSection';
import useViewportSize from '~/hooks/useViewportSize';
import HeaderCell from '~/components/organism/Table/components/Header/components/HeaderCell';
import RowCell from '~/components/organism/Table/components/Row/components/RowCell';
import { AuthContext } from '~/Root/Auth';
import { CHECKBOX_COLUMN_ACCESSOR } from '~/components/organism/Table/components/Header/utils/asCheckboxCell';
import CheckboxColumnCell from '~/components/organism/Table/components/CheckboxColumnCell';
import { type ReturnProps } from '~/components/organism/Table/hooks/useRowSelect';
import { isEmptyObject } from '~/util/object';
import { noop } from 'lodash';
import CheckboxHeaderCell from '~/components/organism/Table/components/Header/components/CheckboxHeaderCell';

const LFA_FLOW_PREFIX = '!LFA';

const text = {
  toggleSortBy: '',
  queryError: Catalog.genericUnknownErrorMessage,
  noSearchResults: Catalog.noResults,
  selectAllOnPage: 'Selecteer alles op pagina',
};

const checkboxOptions = [
  {
    key: 'select-all-on-page',
    label: text.selectAllOnPage,
    payload: 'select-all-on-page',
  },
];

export type SelectedFlow = { flowId: string; flowName: string };

type Props = {
  /** Table columns, column grouping limited to at most one level */
  columns: TableColumnDefinitions;

  /** Table data*/
  data: Array<FlowListData>;
  networkStatus: number;
  loading: boolean;
  error: boolean;
  onSuccessfulDelete: (flowId: string) => void;
  onSuccessfulEnabledChange: (flowId: string, newStatus: boolean) => void;
  options?: {
    renderHeaderCell?: (
      key: string,
      column: TableHeaderCellType<any>,
      isLastColumn: boolean,
    ) => React.ReactNode;
  };
  selectionProps?: ReturnProps<SelectedFlow>;
};

const FlowListTable: React.FCC<Props> = ({
  columns,
  data,
  error,
  loading,
  onSuccessfulDelete,
  onSuccessfulEnabledChange,
  options,
  networkStatus,
  selectionProps = {
    itemsSelected: [],
    allSelected: false,
    selectAllItemsOnPage: f => f,
    toggleRowSelect: () => {},
    checkIfSelected: () => false,
    selectAllItems: () => {},
    deselectAllItems: () => {},
  },
  ...rest
}) => {
  const { isDHAdmin } = useContext(AuthContext);
  const theme = useTheme();
  const [sortSettings] = useSortSettings<Array<string>>('flowSortSettings');
  const resetActions = useResetRecoilState(flowActions);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setHiddenColumns,
    toggleHideAllColumns,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      disableMultiSort: true,
      autoResetSortBy: false,
      autoResetHiddenColumns: false,
      getRowId: ({ id }) => id,
      manualSortBy: true,
      disableSortRemove: true,
      initialState: {
        sortBy: [{ id: sortSettings.id[0], desc: sortSettings.desc }],
      },
    },
    useFlexLayout,
    useSortBy,
  );

  const {
    checkIfSelected = () => false,
    toggleRowSelect = noop,
    allSelected = false,
    selectAllItemsOnPage = (f, z) => ({ f, z }),
    itemsSelected = [],
    deselectAllItems = noop,
  } = selectionProps;
  const isSelectable = !isEmptyObject(selectionProps);

  const viewportSize = useViewportSize();
  useEffect(() => {
    if (viewportSize.width > MAIN_CONTENT_WIDTH) {
      toggleHideAllColumns(false);
      setHiddenColumns(['description']);
    } else {
      setHiddenColumns(['lastModified', 'Statistics', 'description']);
    }
  }, [viewportSize.width, setHiddenColumns, toggleHideAllColumns, theme]);

  const headerCheckboxRef = useRef<HTMLDivElement | null>(null);
  const oneCheckboxSelected = rows.some(row =>
    checkIfSelected({ flowId: row.id, flowName: row.original.name }),
  );

  let bodyComponent;
  if (error) {
    bodyComponent = (
      <StyledNoResultSection data-testid={TEST_ID.ERROR_MESSAGE}>
        {text.queryError}
      </StyledNoResultSection>
    );
  } else if (isInitialLoadStatus(networkStatus)) {
    bodyComponent = (
      <LoadingBody
        rowAmount={rows.length || 10}
        getTableBodyProps={getTableBodyProps}
      />
    );
  } else if (data.length === 0 && !loading) {
    bodyComponent = (
      <StyledNoResultSection>{text.noSearchResults}</StyledNoResultSection>
    );
  } else {
    bodyComponent = (
      <BodyInner {...getTableBodyProps()}>
        {rows.map(row => {
          prepareRow(row);

          const rowProps = { ...row.getRowProps() };
          const flowId = row.original.id;
          const flowName = row.original.name;
          const flowDescription = row.original.description;
          const isDeleted = row.original && row.original.isDeleted === true;
          const runningFlowInstances =
            row.original.Statistics.nonCompletedFlowInstancesTotal;
          const notAllowed = flowName.startsWith(LFA_FLOW_PREFIX) && !isDHAdmin;

          const isRowSelected = checkIfSelected({ flowId, flowName });

          return (
            <Row
              {...rowProps}
              isDeleted={isDeleted}
              isDisabled={notAllowed}
              key={rowProps.key}
              data-objectid={flowId}
              data-deleted={isDeleted}
              dataTestId={TEST_ID.LIST_ITEM}
              onClick={() => resetActions()}
              to={
                notAllowed
                  ? undefined
                  : `/-/automation/flows/builder-v2/update/${flowId}`
              }
            >
              {row.cells.map((cell, idx) => {
                if (cell.column.id === ACTION_COLUMN_ACCESSOR) {
                  return (
                    <FlowActionColumnCell
                      key={`cell-${idx}`}
                      flowName={flowName}
                      flowDescription={flowDescription}
                      flowId={flowId}
                      enabled={row.original.enabled}
                      cell={cell}
                      shared={row.original.shared}
                      onSuccessfulDelete={() => onSuccessfulDelete(flowId)}
                      onSuccessfulEnabledChange={newState =>
                        onSuccessfulEnabledChange(flowId, newState)
                      }
                      runningFlowInstances={runningFlowInstances}
                      disabled={notAllowed}
                      hasErrors={row.original.hasErrors}
                    />
                  );
                }

                if (
                  cell.column.id === CHECKBOX_COLUMN_ACCESSOR &&
                  isSelectable
                ) {
                  return (
                    <CheckboxColumnCell
                      key={`cell-${idx}`}
                      cell={cell}
                      onChange={() => toggleRowSelect({ flowId, flowName })}
                      value={allSelected ? true : isRowSelected}
                      disabled={allSelected}
                    />
                  );
                }

                return <RowCell key={`cell-${idx}`} cell={cell} />;
              })}
            </Row>
          );
        })}
      </BodyInner>
    );
  }

  return (
    <Container {...getTableProps()} {...rest} data-testid={TEST_ID.CONTAINER}>
      <HeaderLarge>
        {headerGroups.map(headerGroup => {
          const headerGroupProps = {
            ...headerGroup.getHeaderGroupProps(),
          };
          return (
            <StyledHeaderTr
              key={headerGroupProps.key}
              data-testid={TEST_ID.HEADER_ROW}
            >
              {headerGroup.headers.map((column, idx) => {
                const isLastColumn = idx === headerGroup.headers.length - 1;
                const headerCellKey = `header-${idx}`;

                if (options && options.renderHeaderCell) {
                  return options.renderHeaderCell(
                    headerCellKey,
                    column,
                    isLastColumn,
                  );
                }

                if (column.id === CHECKBOX_COLUMN_ACCESSOR) {
                  return (
                    <CheckboxHeaderCell
                      key={`header-cell-${idx}`}
                      ref={headerCheckboxRef}
                      column={column}
                      minus={oneCheckboxSelected || allSelected}
                      value={allSelected ? true : oneCheckboxSelected}
                      noRows={rows.length === 0}
                      options={checkboxOptions}
                      onChange={
                        itemsSelected.length > 0 ? deselectAllItems : undefined
                      }
                      onOptionChange={({ option }) => {
                        switch (option.payload) {
                          case 'select-all-on-page':
                            selectAllItemsOnPage(
                              data.map(item => ({
                                flowId: item.id,
                                flowName: item.name,
                              })),
                              !oneCheckboxSelected,
                            );
                            break;
                        }
                      }}
                    />
                  );
                }

                return (
                  <HeaderCell
                    key={headerCellKey}
                    column={column}
                    isLastColumn={isLastColumn}
                  />
                );
              })}
            </StyledHeaderTr>
          );
        })}
      </HeaderLarge>
      <BodyOuter>{bodyComponent}</BodyOuter>
    </Container>
  );
};

const StyledNoResultSection = styled(NoResultSection)<{}>`
  max-width: 800px;
`;

const StyledHeaderTr = styled.div<{}>`
  white-space: nowrap;
  display: flex;
`;

type TableWithStyleProps = {
  onClick?: () => void;
};
const Container = styled.div<TableWithStyleProps>(
  ({ onClick, theme }) => css`
    cursor: ${onClick ? 'pointer' : 'default'};
    user-select: none;
    border-spacing: 0;
    border-radius: ${theme.getTokens().border.radius.m};
    border: 1px solid ${theme.color('tertiary', 'light')};
    margin-bottom: ${theme.space('l')};
    box-shadow: ${theme.boxShadow('card')};
  `,
);

const defaultColumn = {
  minWidth: 20,
  width: 120,
  maxWidth: 200,
};

export default FlowListTable;
